import { z } from 'zod'
import { type Expand } from '@/types/ui/expand'
import { zodValidationHelpers } from '@/lib/utils/zod-validation-helpers'

// Base schema for milestone form data
const baseMilestoneSchema = z.object({
  id: z.number(),
  startDate: z.date().nullable(),
  endDate: z.date().nullable(),
  status: z.string().min(1, 'Field is required'),
  amount: z.number().nullable(),
  internalBudget: z.number().nullable(),
  reference: z.string().min(1, 'Field is required'),
  description: z.string().optional(),
})

// Factory function to create schema with conditional validation
export const createMilestoneFormSchema = (requireAccountingFields: boolean = false) => {
  return baseMilestoneSchema.superRefine((data, ctx) => {
    // Cache values to avoid repeated property access
    const { startDate, endDate } = data
    
    // Amount validations
    zodValidationHelpers.conditionalRequiredDecimal(ctx, ['amount'], data.amount, requireAccountingFields)
    zodValidationHelpers.conditionalRequiredDecimal(ctx, ['internalBudget'], data.internalBudget, requireAccountingFields)

    // Required date validations
    if (!startDate) {
      zodValidationHelpers.requiredDate(ctx, ['startDate'])
    }
    
    if (!endDate) {
      zodValidationHelpers.requiredDate(ctx, ['endDate'])
    }
    
    // Date range validation - only validate when both dates are present
    if (startDate && endDate && endDate <= startDate) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'End date must be after start date',
        path: ['endDate'],
      })
    }
  })
}

// Factory function for milestone array schema
export const createMilestoneFormArraySchema = (requireAccountingFields: boolean = false) => {
  return z.object({
    milestones: z.array(createMilestoneFormSchema(requireAccountingFields))
  })
}

// Automatically infer the TypeScript type from the base schema
export type MilestoneFormData = Expand<z.infer<typeof baseMilestoneSchema>>
export type MilestoneFormArrayData = {
  milestones: MilestoneFormData[]
} 